import AuthRepository from '@/repositories/AuthRepository';
import {
  browserSupportsWebAuthnAutofill,
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser';
import { computed, ref } from 'vue';

/**
 * Composable for WebAuthn passkey authentication and management.
 *
 * Provides methods for:
 * - Checking browser support and credential existence
 * - Passkey login (challenge + assertion)
 * - Passkey registration for authenticated users
 * - Credential management (list, delete)
 *
 * @returns {Object} WebAuthn state and methods
 */
const webAuthnDisabled = process.env.VUE_APP_DISABLE_WEBAUTHN === 'true';

export function useWebAuthn() {
  const isLoading = ref(false);
  // True only while a signed assertion is being verified; a new challenge
  // request then would replace the one the server is about to check.
  const isVerifying = ref(false);
  const error = ref(null);
  const isSupported = computed(() => !webAuthnDisabled && browserSupportsWebAuthn());

  /**
   * Check if a user (by email) has registered passkey credentials.
   *
   * @param {string} email
   * @returns {Promise<boolean>}
   */
  async function hasCredential(email) {
    if (!email) return false;
    try {
      const response = await AuthRepository.webAuthnHasCredential(email);
      return response.data.has_credential === true;
    } catch {
      return false;
    }
  }

  /**
   * Authenticate with a passkey. Returns the token response data.
   *
   * @param {string} [email] - Optional email to scope the challenge
   * @param {Object} [payload] - Additional fields to include with the assertion
   * @param {Object} [authenticationOptions] - Browser authentication options
   * @returns {Promise<{access_token: string, token_type: string, expires_in: number}>}
   * @throws {Error} If the ceremony fails or the server rejects the assertion
   */
  async function authenticate(email, payload = {}, authenticationOptions = {}) {
    error.value = null;
    isLoading.value = true;

    try {
      // Step 1: Get challenge from server
      const challengeResponse = await AuthRepository.webAuthnChallenge(email);
      const challengeOptions = challengeResponse.data;

      // Step 2: Browser performs the WebAuthn ceremony (biometric prompt)
      const assertion = await startAuthentication({
        optionsJSON: challengeOptions,
        ...authenticationOptions,
      });

      // Step 3: Send assertion to server for verification and token issuance
      isVerifying.value = true;
      const tokenResponse = await AuthRepository.webAuthnAuthenticate({
        ...assertion,
        ...payload,
      });
      return tokenResponse.data;
    } catch (err) {
      // User cancelled the ceremony
      if (err.name === 'NotAllowedError') {
        error.value = 'passkey_cancelled';
        throw err;
      }
      // Server-side validation error
      if (err.response?.data?.errors) {
        error.value = err.response.data.errors;
        throw err;
      }
      error.value = err.message || 'passkey_failed';
      throw err;
    } finally {
      isLoading.value = false;
      isVerifying.value = false;
    }
  }

  async function supportsAutofill() {
    if (!isSupported.value) return false;
    try {
      return await browserSupportsWebAuthnAutofill();
    } catch {
      return false;
    }
  }

  /**
   * Register a new passkey for the currently authenticated user.
   *
   * @returns {Promise<void>}
   * @throws {Error} If the registration ceremony fails
   */
  async function register() {
    error.value = null;
    isLoading.value = true;

    try {
      // Step 1: Get registration options from server
      const optionsResponse = await AuthRepository.webAuthnRegisterOptions();
      const registrationOptions = optionsResponse.data;

      // Step 2: Browser performs the registration ceremony
      const attestation = await startRegistration({ optionsJSON: registrationOptions });

      // Step 3: Send attestation to server to save the credential
      await AuthRepository.webAuthnRegister(attestation);
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        error.value = 'passkey_cancelled';
        throw err;
      }
      error.value = err.message || 'registration_failed';
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * List all registered passkey credentials for the current user.
   *
   * @returns {Promise<Array<{id: string, alias: string, created_at: string, last_used_at: string, is_enabled: boolean}>>}
   */
  async function listCredentials() {
    const response = await AuthRepository.webAuthnListCredentials();
    return response.data.data;
  }

  /**
   * Delete a specific passkey credential.
   *
   * @param {string} credentialId
   * @returns {Promise<void>}
   */
  async function deleteCredential(credentialId) {
    await AuthRepository.webAuthnDeleteCredential(credentialId);
  }

  return {
    isSupported,
    isLoading,
    isVerifying,
    error,
    supportsAutofill,
    hasCredential,
    authenticate,
    register,
    listCredentials,
    deleteCredential,
  };
}
